// Choosing how to pay for an arbiter account, before anything is paid.
//
// One dialog, two moments:
//
//   'signup'    after "I am an Arbiter", before the account exists. The plan
//               chosen here rides along with the sign-up (accountApi stores it
//               in billing/{uid}) and nothing is paid yet.
//   'checkout'  after the first confirmed sign-in, with that plan selected:
//               "Complete your subscription", and on to Stripe Checkout.
//
// Either way the arbiter sees what they are paying for — price with VAT,
// renewal, the trial and the right of withdrawal — before Stripe's page, which
// then only confirms it.
//
// Before launch the sign-up path is reached only with `?signup=paid` on the
// address (remembered for the tab), so it can be tried without showing it to
// everyone; the checkout still needs an admin account until BILLING_OPEN
// (functions/lib/billing.js).
//
// No imports from state.js or firebase-auth.js: firebase-auth.js imports this.

import { FUNCTIONS_BASE, callFunction, currentLang } from '../services/functions-api.js';
import { renderWithdrawalInfo } from './withdrawal-info.js';

const tr = (s, p) => (window.tr ? window.tr(s, p) : s);

const PLAN_TEXT = {
    monthly: { name: 'Monthly', per: '{price} kr / month', note: 'Cancel whenever you like' },
    quarterly: { name: 'Every 3 months', per: '{price} kr / 3 months', note: 'About {month} kr a month' },
    yearly: { name: 'Yearly', per: '{price} kr / year', note: 'About {month} kr a month' }
};

/** The plan offered first on sign-up: the one that costs least per month. */
export const DEFAULT_PLAN = 'yearly';

let plansPromise = null;
let loadedPlans = null;

/** What is sold, from billingApi — no account needed. Cached for the page; a failure is retried next time. */
export function loadPlans() {
    if (!plansPromise) {
        plansPromise = fetch(`${FUNCTIONS_BASE}/billingApi`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'plans' })
        })
            .then(res => (res.ok ? res.json() : null))
            .catch(() => null)
            .then(data => {
                if (data && Array.isArray(data.plans)) loadedPlans = data;
                else plansPromise = null;
                return loadedPlans;
            });
    }
    return plansPromise;
}

const PREVIEW_KEY = 'zw-paid-signup';

function previewRequested() {
    try {
        if (new URLSearchParams(window.location.search).get('signup') === 'paid') {
            sessionStorage.setItem(PREVIEW_KEY, '1');
        }
        return sessionStorage.getItem(PREVIEW_KEY) === '1';
    } catch (e) {
        return false;
    }
}

// Noted at load, before anything has a chance to tidy the address bar: the
// question itself is only asked once somebody opens sign-in or sign-up.
previewRequested();

/**
 * Does "I am an Arbiter" mean choosing a plan (subscriptions are open, or the
 * preview was asked for) rather than typing an access code?
 */
export async function paidSignupActive() {
    if (previewRequested()) {
        await loadPlans();
        return !!loadedPlans;
    }
    const plans = await loadPlans();
    return !!(plans && plans.open);
}

function formatKroner(ore) {
    const kr = ore / 100;
    return Number.isInteger(kr) ? String(kr) : kr.toFixed(2).replace('.', ',');
}

function monthsIn(plan) {
    return plan.interval === 'year' ? 12 * plan.intervalCount : plan.intervalCount;
}

function perMonth(plan) {
    return plan.amount / monthsIn(plan);
}

function planById(id) {
    return loadedPlans ? loadedPlans.plans.find(p => p.id === id) || null : null;
}

/** "Yearly · 349 kr / year · 14 days free" — the line the sign-up form shows under its fields. */
export function planSummary(id) {
    const plan = planById(id);
    const text = PLAN_TEXT[id];
    if (!plan || !text) return '';
    return [
        tr(text.name),
        tr(text.per, { price: formatKroner(plan.amount) }),
        tr('{n} days free', { n: loadedPlans.trialDays })
    ].join(' · ');
}

/** "From 29 kr a month · 14 days free", for the arbiter card on the sign-up page. */
export function cheapestLine() {
    if (!loadedPlans || !loadedPlans.plans.length) return '';
    const lowest = Math.min(...loadedPlans.plans.map(perMonth));
    return tr('From {price} kr a month · {n} days free', { price: Math.floor(lowest / 100), n: loadedPlans.trialDays });
}

// ===== The dialog ============================================================

const els = () => ({
    modal: document.getElementById('plan-choice-modal'),
    title: document.getElementById('plan-choice-title'),
    lead: document.getElementById('plan-choice-lead'),
    list: document.getElementById('plan-choice-list'),
    terms: document.getElementById('plan-choice-terms'),
    startRow: document.getElementById('plan-choice-start-now-row'),
    startBox: document.getElementById('plan-choice-start-now'),
    startText: document.getElementById('plan-choice-start-now-text'),
    withdrawal: document.getElementById('plan-choice-withdrawal-body'),
    go: document.getElementById('btn-plan-choice-continue'),
    close: document.getElementById('close-plan-choice'),
    codeRow: document.getElementById('plan-choice-code-row'),
    codeLink: document.getElementById('plan-choice-code-link')
});

let current = null;

function renderList(e) {
    const monthly = planById('monthly');
    e.list.replaceChildren();
    for (const plan of loadedPlans.plans) {
        const text = PLAN_TEXT[plan.id];
        if (!text) continue;

        const card = document.createElement('button');
        card.type = 'button';
        card.setAttribute('role', 'radio');
        const on = plan.id === current.selected;
        card.setAttribute('aria-checked', on ? 'true' : 'false');
        card.style.cssText = `display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; width: 100%; text-align: left;
            padding: 0.85rem 1rem; border-radius: 12px; cursor: pointer; font: inherit; color: var(--text-main);
            background: ${on ? 'var(--surface-tint-hover)' : 'transparent'};
            border: 1.5px solid ${on ? 'var(--primary)' : 'var(--border-color)'};`;

        const dot = document.createElement('span');
        dot.style.cssText = `flex: none; width: 16px; height: 16px; border-radius: 50%; box-sizing: border-box;
            border: ${on ? '5px solid var(--primary)' : '1.5px solid var(--text-muted)'};`;

        const name = document.createElement('div');
        const title = document.createElement('div');
        title.style.cssText = 'font-weight: 600;';
        title.textContent = tr(text.name);
        const save = monthly && plan.id !== 'monthly' ? Math.round((1 - perMonth(plan) / perMonth(monthly)) * 100) : 0;
        if (save > 0) {
            const badge = document.createElement('span');
            badge.style.cssText = 'margin-left: 0.4rem; font-size: 0.72rem; font-weight: 600; padding: 0.1rem 0.5rem; border-radius: 99px; background: rgba(16, 185, 129, 0.12); color: var(--success);';
            badge.textContent = tr('Save {n} %', { n: save });
            title.append(badge);
        }
        const note = document.createElement('div');
        note.style.cssText = 'font-size: 0.8rem; color: var(--text-muted);';
        note.textContent = tr(text.note, { month: Math.round(perMonth(plan) / 100) });
        name.append(title, note);

        const left = document.createElement('div');
        left.style.cssText = 'display: flex; align-items: center; gap: 0.75rem;';
        left.append(dot, name);

        const price = document.createElement('div');
        price.style.cssText = 'font-weight: 600; white-space: nowrap;';
        price.textContent = tr(text.per, { price: formatKroner(plan.amount) });

        card.append(left, price);
        card.addEventListener('click', () => {
            current.selected = plan.id;
            render();
        });
        e.list.append(card);
    }
}

function render() {
    const e = els();
    const trial = !current.trialUsed;
    const n = loadedPlans.trialDays;

    if (current.mode === 'signup') {
        e.title.textContent = tr('Choose a Plan');
        e.lead.textContent = tr('Every plan gives you the same thing: a full arbiter account. The first {n} days are free, and you can cancel at any time before they end.', { n });
    } else {
        e.title.textContent = tr('Complete Your Subscription');
        e.lead.textContent = trial
            ? tr('This is the plan you chose when you created your account. Check it, then go on to payment with Stripe. The first {n} days are free, and your arbiter rights start with them.', { n })
            : tr('Choose a plan, then go on to payment with Stripe. Your arbiter rights start as soon as it is paid.');
    }

    renderList(e);

    // No word about VAT: the seller is not registered for it (seller.js), so the
    // prices are simply what is paid.
    e.terms.textContent = trial
        ? tr('After the free trial the subscription renews automatically until you cancel. You can withdraw within {n} days of subscribing (angrerett) and then pay nothing.', { n: loadedPlans.withdrawalDays })
        : tr('The subscription renews automatically until you cancel. You can withdraw within {n} days of subscribing (angrerett).', { n: loadedPlans.withdrawalDays });

    // Access codes keep working after launch: the way to one from sign-up.
    e.codeRow.style.display = current.mode === 'signup' && current.onUseCode ? 'block' : 'none';

    // Once the trial is spent the paid service starts inside the withdrawal
    // period, which needs the arbiter's explicit request (angrerettloven § 19).
    const needsStartNow = current.mode === 'checkout' && !trial;
    e.startRow.style.display = needsStartNow ? 'flex' : 'none';
    e.startText.textContent = tr('Start my subscription straight away. I understand that if I withdraw within {n} days, I pay for the days I have used.', { n: loadedPlans.withdrawalDays });

    renderWithdrawalInfo(e.withdrawal);

    const name = PLAN_TEXT[current.selected] ? tr(PLAN_TEXT[current.selected].name).toLowerCase() : '';
    e.go.textContent = current.mode === 'signup'
        ? tr('Continue with {plan}', { plan: name })
        : tr('Go to Payment');
    e.go.disabled = needsStartNow && !e.startBox.checked;
}

function hide() {
    const e = els();
    if (e.modal) e.modal.classList.remove('active');
}

async function goOn() {
    const e = els();
    if (current.mode === 'signup') {
        const done = current.onChoose;
        const plan = current.selected;
        hide();
        if (done) done(plan);
        return;
    }

    e.go.disabled = true;
    const res = await callFunction('billingApi', {
        action: 'checkout',
        plan: current.selected,
        startRequested: e.startBox.checked,
        lang: currentLang()
    });
    if (res.ok && res.url) {
        window.location.assign(res.url);
        return;
    }
    e.go.disabled = false;

    const code = res.error && typeof res.error === 'object' ? res.error.code : res.error;
    let message = tr('Could not reach the payment page. Please try again shortly.');
    if (code === 'billing_closed') message = tr('Subscriptions are not open yet.');
    if (code === 'already_subscribed') message = tr('You already have a subscription. You can manage it under Settings.');
    if (code === 'start_not_requested') message = tr('Tick the box to start your subscription straight away first.');
    if (window.Dialog) window.Dialog.error(message);
}

let wired = false;

function wire() {
    if (wired) return;
    const e = els();
    if (!e.modal) return;
    wired = true;
    e.go.addEventListener('click', goOn);
    e.close.addEventListener('click', () => {
        const cancelled = current && current.onCancel;
        hide();
        if (cancelled) cancelled();
    });
    e.startBox.addEventListener('change', render);
    e.codeLink.addEventListener('click', (ev) => {
        ev.preventDefault();
        const useCode = current && current.onUseCode;
        hide();
        if (useCode) useCode();
    });
}

/**
 * Open the chooser.
 *
 * @param {object}   o
 * @param {'signup'|'checkout'} o.mode
 * @param {string=}  o.selected   plan to start on (default DEFAULT_PLAN)
 * @param {boolean=} o.trialUsed  checkout only: the trial is spent, so ask for "start now"
 * @param {function=} o.onChoose  signup only: called with the plan id
 * @param {function=} o.onCancel  called if the dialog is closed without choosing
 * @param {function=} o.onUseCode signup only: "Have an access code?" was chosen instead
 * @returns {Promise<boolean>} false if the plans could not be loaded
 */
export async function openPlanChoice(o) {
    const plans = await loadPlans();
    if (!plans) {
        if (window.Dialog) window.Dialog.error(tr('Could not load the plans. Check your connection and try again.'));
        return false;
    }
    wire();
    const selected = planById(o.selected) ? o.selected : DEFAULT_PLAN;
    current = Object.assign({ trialUsed: false }, o, { selected });
    const e = els();
    e.startBox.checked = false;
    render();
    e.modal.classList.add('active');
    return true;
}
